import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";

import { AlertCircle, ChevronUp, ChevronDown } from "lucide-react";

interface ErrorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  errorList: Array<{
    rut: string;
    nombre: string;
    errors: string[];
    beneficiarioId: number | string;
  }>;
  errorMessage: string;
  expandedItems: Set<string>;
  onToggleItem: (rut: string) => void;
}

export const ErrorDialog: React.FC<ErrorDialogProps> = ({
  open,
  onOpenChange,
  errorList,
  errorMessage,
  expandedItems,
  onToggleItem,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-1xl md:max-w-2xl lg:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[var(--color-z-blue)] sm:text-lg">
            {"Errores "}
          </DialogTitle>
        </DialogHeader>
        <div className="relative p-6 pb-4">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-800 mb-2">
              Hay errores en los datos
            </h2>
            <p className="text-slate-600 text-sm">
              Revisa la información de los beneficiarios antes de continuar.
            </p>
          </div>
        </div>

        <div className="px-6 pb-4">
          <div className="flex items-center justify-between mb-4">
            <span className="bg-red-100 text-red-700 text-xs font-medium px-2.5 py-1 rounded-full">
              {errorMessage}
            </span>
          </div>

          <div className="space-y-3 max-h-60 overflow-y-auto">
            {errorList.map((group) => {
              const isExpanded = expandedItems.has(group.rut);
              return (
                <div
                  key={group.rut}
                  className="bg-white rounded-lg border border-slate-200 overflow-hidden"
                >
                  <button
                    onClick={() => onToggleItem(group.rut)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                        <span className="text-red-600 font-semibold text-sm">
                          {group.errors.length}
                        </span>
                      </div>
                      <div className="text-left">
                        <span className="text-sm font-medium text-slate-700 block">
                          {group.nombre}
                        </span>
                        <span className="text-xs text-slate-500">
                          ID: {group.beneficiarioId}
                        </span>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-3 border-t border-slate-100">
                      <div className="pl-11 space-y-2 pt-2">
                        {group.errors.map((error, index) => (
                          <div
                            key={index}
                            className="text-sm text-slate-600 flex items-start gap-2"
                          >
                            <span className="text-red-500 mt-1">•</span>
                            <span className="flex-1">{error}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="px-6 pb-6 pt-4 border-t border-slate-200">
          <div className="text-center mb-4">
            <p className="text-sm text-red-600">
              Si el problema persiste, contacta al soporte.
            </p>
          </div>
          <Button
            onClick={() => onOpenChange(false)}
            className="w-full text-white font-medium py-3 rounded-lg bg-[var(--color-z-blue)] hover:bg-[var(--color-z-blue-dark)]"
          >
            Entendido
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
